<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <div class="user-menu-dropdown" ref="dropdownRef">
    <!-- Dropdown Trigger Button -->
    <button
      class="user-menu-trigger"
      @click="toggleDropdown"
      :aria-expanded="isOpen"
      aria-haspopup="true"
      :aria-label="$t('navigation.userMenu')"
    >
      <span class="user-menu-trigger__name">{{ userName }}</span>
      <span class="user-menu-trigger__icon" :class="{ open: isOpen }">▼</span>
    </button>

    <!-- Dropdown Menu - Teleported to body to escape stacking context -->
    <Teleport to="body">
      <Transition name="dropdown-fade">
        <div
          v-if="isOpen"
          class="user-menu-dropdown__menu"
          role="menu"
          :aria-label="$t('navigation.userMenu')"
          :style="dropdownStyle"
        >
          <!-- User Info Section -->
          <div class="user-menu-dropdown__header" role="presentation">
            <div class="user-menu-dropdown__user-name">{{ userName }}</div>
            <div class="user-menu-dropdown__user-email">{{ userEmail }}</div>
            <span class="user-menu-dropdown__plan" :class="isPremium ? 'premium' : 'free'">
              {{ isPremium ? $t('subscription.premium') : $t('subscription.free') }}
            </span>
          </div>

          <div class="user-menu-dropdown__divider" role="separator"></div>

          <!-- Navigation Links -->
          <router-link
            to="/settings"
            class="user-menu-dropdown__item"
            role="menuitem"
            @click="closeDropdown"
          >
            <span class="user-menu-dropdown__item-icon">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </span>
            {{ $t('navigation.settings') }}
          </router-link>

          <router-link
            to="/support"
            class="user-menu-dropdown__item"
            role="menuitem"
            @click="closeDropdown"
          >
            <span class="user-menu-dropdown__item-icon">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </span>
            {{ $t('navigation.support') }}
          </router-link>

          <router-link
            to="/extension"
            class="user-menu-dropdown__item"
            role="menuitem"
            @click="closeDropdown"
          >
            <span class="user-menu-dropdown__item-icon">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
              </svg>
            </span>
            {{ $t('navigation.extension') }}
          </router-link>

          <div class="user-menu-dropdown__divider" role="separator"></div>

          <!-- Sign Out Button -->
          <button
            class="user-menu-dropdown__item user-menu-dropdown__item--danger"
            role="menuitem"
            @click="handleSignOut"
            :disabled="isSigningOut"
          >
            <span class="user-menu-dropdown__item-icon" v-if="!isSigningOut">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </span>
            <span class="spinner-icon" v-else></span>
            {{ isSigningOut ? $t('auth.signingOut') : $t('auth.signOut') }}
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { useRoute } from 'vue-router'

interface Props {
  userName: string
  userEmail: string
  isPremium: boolean
  isSigningOut: boolean
}

defineProps<Props>()
const emit = defineEmits<{
  signOut: []
}>()

const route = useRoute()
const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
const dropdownPosition = ref({ top: 0, left: 0, right: 0 })

// Computed style for dropdown positioning
const dropdownStyle = computed(() => ({
  position: 'fixed' as const,
  top: `${dropdownPosition.value.top}px`,
  right: `${dropdownPosition.value.right}px`
}))

// Calculate dropdown position based on trigger button
function updateDropdownPosition() {
  if (!dropdownRef.value) return

  const triggerButton = dropdownRef.value.querySelector('.user-menu-trigger') as HTMLElement
  if (!triggerButton) return

  const rect = triggerButton.getBoundingClientRect()
  const scrollY = window.scrollY || window.pageYOffset

  // Position dropdown below the trigger button, aligned to the right
  dropdownPosition.value = {
    top: rect.bottom + scrollY + 8, // 8px gap below trigger
    left: rect.left,
    right: window.innerWidth - rect.right // Distance from right edge
  }
}

// Toggle dropdown open/close
function toggleDropdown() {
  if (!isOpen.value) {
    updateDropdownPosition()
  }
  isOpen.value = !isOpen.value
}

// Close dropdown
function closeDropdown() {
  isOpen.value = false
}

// Handle sign out
function handleSignOut() {
  emit('signOut')
  closeDropdown()
}

// Handle click outside to close dropdown
function handleClickOutside(event: MouseEvent) {
  if (dropdownRef.value && event.target instanceof HTMLElement && !dropdownRef.value.contains(event.target)) {
    closeDropdown()
  }
}

// Handle keyboard navigation
function handleKeydown(event: KeyboardEvent) {
  if (!isOpen.value) return

  // Close on Escape
  if (event.key === 'Escape') {
    closeDropdown()
  }

  // Arrow key navigation
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault()
    navigateMenu(event.key === 'ArrowDown' ? 1 : -1)
  }
}

// Navigate menu items with arrow keys
function navigateMenu(direction: number) {
  if (!dropdownRef.value) return

  const menuItems = Array.from(
    dropdownRef.value.querySelectorAll<HTMLElement>(
      '.user-menu-dropdown__item:not([disabled])'
    )
  )

  const currentIndex = menuItems.findIndex((item) => item === document.activeElement)
  let nextIndex = currentIndex + direction

  // Wrap around
  if (nextIndex < 0) nextIndex = menuItems.length - 1
  if (nextIndex >= menuItems.length) nextIndex = 0

  menuItems[nextIndex]?.focus()
}

// Auto-close dropdown on route change
watch(
  () => route.path,
  () => {
    closeDropdown()
  }
)

// Handle window resize to update dropdown position
function handleResize() {
  if (isOpen.value) {
    updateDropdownPosition()
  }
}

// Add/remove event listeners
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', handleResize)
  window.addEventListener('scroll', handleResize, true) // Capture phase to catch all scrolls
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('scroll', handleResize, true)
})
</script>

<style lang="scss" scoped>
.user-menu-dropdown {
  position: relative;
}

// Trigger button
.user-menu-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--nw-bg-secondary);
  border: 1px solid var(--nw-border-light);
  border-radius: var(--nw-radius-md);
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  color: var(--nw-text-primary);

  &:hover {
    background: var(--nw-bg-tertiary);
    border-color: var(--nw-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--nw-primary);
    outline-offset: 2px;
    box-shadow: var(--nw-focus-ring);
  }

  // Tablet landscape: smaller size
  @media (max-width: 1024px) and (min-width: 769px) {
    padding: 0.4rem 0.6rem;
    font-size: 0.9rem;
  }
}

.user-menu-trigger__name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;

  @media (max-width: 1024px) and (min-width: 769px) {
    max-width: 120px;
  }
}

.user-menu-trigger__icon {
  font-size: 0.75rem;
  transition: transform 0.2s ease;

  &.open {
    transform: rotate(180deg);
  }
}

// Dropdown menu - uses fixed positioning via Teleport to escape stacking context
.user-menu-dropdown__menu {
  position: fixed;
  min-width: 250px;
  background: var(--nw-bg-primary);
  border: 1px solid var(--nw-border-light);
  border-radius: var(--nw-radius-lg);
  box-shadow: var(--nw-shadow-xl);
  z-index: var(--nw-z-popover, 1060);
  overflow: hidden;
}

// Header section
.user-menu-dropdown__header {
  padding: 1rem;
  background: var(--nw-bg-secondary);
}

.user-menu-dropdown__user-name {
  font-weight: 600;
  color: var(--nw-text-primary);
  margin-bottom: 0.25rem;
}

.user-menu-dropdown__user-email {
  font-size: 0.875rem;
  color: var(--nw-text-secondary);
  margin-bottom: 0.5rem;
  word-break: break-all;
}

.user-menu-dropdown__plan {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: var(--nw-radius-xl);
  font-size: 0.75rem;
  font-weight: 600;

  &.free {
    background: var(--nw-secondary);
    color: var(--nw-text-inverse);
  }

  &.premium {
    background: var(--nw-success);
    color: var(--nw-text-inverse);
  }
}

// Divider
.user-menu-dropdown__divider {
  height: 1px;
  background: var(--nw-border-light);
}

// Menu items
.user-menu-dropdown__item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;
  color: var(--nw-text-primary);
  text-decoration: none;
  font-size: 0.9375rem;

  &:hover:not(:disabled) {
    background: var(--nw-bg-secondary);
  }

  &:focus-visible {
    outline: 2px solid var(--nw-primary);
    outline-offset: -2px;
    background: var(--nw-primary-lightest);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &--danger {
    color: var(--nw-error);

    &:hover:not(:disabled) {
      background: var(--nw-error-lightest);
    }
  }
}

.user-menu-dropdown__item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
}

// Spinner icon for loading state
.spinner-icon {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--nw-error);
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// Dropdown animation
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.2s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>

